// Attachment rendered inside a sent user message.
// • Images — inline thumbnail, click → lightbox full-size view
// • PDFs   — card with PDF badge, click → lightbox iframe
// • Others — type-aware icon pill with name/size, click → lightbox info card
//
// The file is shown from the page's own copy while it has one (just sent), and
// otherwise through the conversation's attachment route via useApiFileUrl, so
// it loads on every client — token clients get an authenticated blob URL.
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useChatConversationId, type ChatAttachment } from '@/lib/chatStore';
import { getFileTypeStyle } from '@/lib/fileTypeIcon';
import { lightboxStore } from '@/lib/lightboxStore';
import { formatBytes as formatFileSize } from '@/lib/format';
import { attachmentApiPath, useApiFileUrl } from '@/lib/useApiFileUrl';

/** A loadable URL for the attachment: its local copy, else its route. */
function useAttachmentUrl(attachment: ChatAttachment): { url: string | null; error: string | null } {
  const conversationId = useChatConversationId();
  const remote = useApiFileUrl(
    attachment.localUrl || !conversationId ? null : attachmentApiPath(conversationId, attachment.id),
  );
  return attachment.localUrl ? { url: attachment.localUrl, error: null } : remote;
}

export function UserAttachment({ attachment }: { attachment: ChatAttachment }) {
  const { url, error } = useAttachmentUrl(attachment);
  const mime = attachment.mimeType;
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf';
  const { Icon, color } = getFileTypeStyle(mime);
  const open = (): void => {
    if (url) lightboxStore.open({ url, fileName: attachment.name, mimeType: mime, downloadUrl: url });
  };

  if (error) {
    return (
      <div
        className="flex items-center gap-1.5 rounded-lg bg-primary-foreground/10 px-2 py-1.5 text-xs text-primary-foreground"
        title={error}
      >
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span className="max-w-[160px] truncate">{attachment.name}</span>
        <span className="text-primary-foreground/60">unavailable</span>
      </div>
    );
  }

  // ── Image: inline thumbnail, click → lightbox ──────────────────────────────
  if (isImage) {
    return url ? (
      <button
        type="button"
        className="block overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={open}
        title="Click to enlarge"
      >
        <img
          src={url}
          alt={attachment.name}
          className="max-h-48 max-w-full rounded-lg object-contain transition-opacity hover:opacity-90"
          loading="lazy"
        />
      </button>
    ) : (
      <div
        className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary-foreground/10"
        aria-label="Loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-primary-foreground/60" />
      </div>
    );
  }

  // ── PDF: card, click → lightbox iframe ──────────────────────────────────────
  if (isPdf) {
    return (
      <button
        type="button"
        className="flex items-center gap-2 rounded-lg border border-primary-foreground/20 bg-red-500/10 px-2.5 py-2 transition-colors hover:bg-red-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={open}
        disabled={!url}
        title="Click to preview PDF"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-red-500/20 text-xs font-bold text-red-400">
          PDF
        </div>
        <div className="flex min-w-0 flex-col items-start">
          <span className="max-w-[160px] truncate text-xs font-medium text-primary-foreground">{attachment.name}</span>
          <span className="text-xs text-primary-foreground/60">{formatFileSize(attachment.size)}</span>
        </div>
      </button>
    );
  }

  // ── Everything else: type-aware icon pill, click → lightbox info card ───────
  return (
    <button
      type="button"
      className="flex items-center gap-1.5 rounded-lg bg-primary-foreground/10 px-2 py-1.5 transition-colors hover:bg-primary-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onClick={open}
      disabled={!url}
      title={`Open ${attachment.name}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${color}`} />
      <span className="max-w-[140px] truncate text-xs text-primary-foreground">{attachment.name}</span>
      <span className="text-xs text-primary-foreground/60">{formatFileSize(attachment.size)}</span>
    </button>
  );
}
